import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { showTokenExpiredAlert, getConfig, authHeader, getCustId, isApiSuccess } from "utils";

// GET subscriber/getCasesByCustomer/{custId} -> dataList (null when no tickets)
export const ticketList = createAsyncThunk('ticket/list', async ({ navigate }) => {
    try {
        const res = await axios({
            method: 'GET',
            url: getConfig.API_URL + `subscriber/getCasesByCustomer/${getCustId()}`,
            headers: authHeader()
        })
        return res
    } catch (error) {
        return error.response
    }
})

// Case Reason dropdown: GET subscriber/caseReason/isCustomerAllowed -> dataList [{ reasonId, name, ... }]
export const caseReasonList = createAsyncThunk('ticket/caseReason', async ({ navigate }) => {
    try {
        const res = await axios({
            method: 'GET',
            url: getConfig.API_URL + 'subscriber/caseReason/isCustomerAllowed',
            headers: authHeader()
        })
        return res
    } catch (error) {
        return error.response
    }
})

// Ticket details (remarks history page): GET subscriber/getCase/{caseId} -> data
export const ticketDetails = createAsyncThunk('ticket/details', async ({ id, navigate }) => {
    try {
        const res = await axios({
            method: 'GET',
            url: getConfig.API_URL + `subscriber/getCase/${id}`,
            headers: authHeader()
        })
        return res
    } catch (error) {
        return error.response
    }
})

// POST subscriber/createCase — payload exactly as the portal sends it.
// (name / username / email / mobile are shown disabled in the form and not sent)
export const addTicket = createAsyncThunk('ticket/add', async (data) => {
    try {
        const res = await axios({
            method: 'POST',
            url: getConfig.API_URL + 'subscriber/createCase',
            data: {
                "caseTitle": data?.values?.caseTitle,
                "caseType": "Issue",
                "caseReasonId": data?.values?.caseReasonId,
                "firstRemark": data?.values?.firstRemark,
                "customersId": getCustId(),
                "caseFor": "Customer",
                "caseOrigin": "Self Care",
                "priority": "Medium",
            },
            headers: authHeader()
        })
        return res
    } catch (error) {
        return error.response
    }
})

// POST subscriber/updateCaseDetails/updateDetails — multipart FormData with
// "caseUpdate" = JSON { remark, ticketId, remarkType: "Public" }
export const addTicketRemark = createAsyncThunk('ticket/addRemark', async (data) => {
    try {
        const formData = new FormData()
        formData.append('caseUpdate', JSON.stringify({
            "remark": data?.values?.remark,
            "ticketId": data?.values?.ticketId,
            "remarkType": "Public",
        }))
        const res = await axios({
            method: 'POST',
            url: getConfig.API_URL + 'subscriber/updateCaseDetails/updateDetails',
            data: formData,
            headers: { "Authorization": authHeader().Authorization }
        })
        return res
    } catch (error) {
        return error.response
    }
})

const ticket = createSlice({
    name: 'ticket',
    initialState: {
        list: [],
        caseReason: [],
        details: null,
        loading: false,
        reasonLoading: false,
    },
    extraReducers: (builder) => {
        builder.addCase(ticketList.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(ticketList.fulfilled, (state, action) => {
            state.loading = false;
            if (isApiSuccess(action?.payload)) {
                state.list = action.payload.data.dataList || [];
            } else if (action?.payload?.status === 401) {
                showTokenExpiredAlert(action?.payload?.data?.message, action.meta.arg.navigate);
            } else {
                state.list = []
            }
        });
        builder.addCase(ticketList.rejected, (state) => {
            state.loading = false;
        });

        builder.addCase(caseReasonList.pending, (state) => {
            state.reasonLoading = true;
        });
        builder.addCase(caseReasonList.fulfilled, (state, action) => {
            state.reasonLoading = false;
            if (isApiSuccess(action?.payload)) {
                state.caseReason = action.payload.data.dataList || [];
            } else if (action?.payload?.status === 401) {
                showTokenExpiredAlert(action?.payload?.data?.message, action.meta.arg.navigate);
            } else {
                state.caseReason = []
            }
        });
        builder.addCase(caseReasonList.rejected, (state) => {
            state.reasonLoading = false;
        });

        builder.addCase(ticketDetails.fulfilled, (state, action) => {
            if (isApiSuccess(action?.payload)) {
                state.details = action.payload.data.data;
            } else if (action?.payload?.status === 401) {
                showTokenExpiredAlert(action?.payload?.data?.message, action.meta.arg.navigate);
            } else {
                state.details = null
            }
        });
    }
})

export default ticket.reducer;
